
import React, { useEffect, useRef, useState } from 'react'
import { db, storage } from '../../firebase'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import countries from '../../arrays/countries.json'
import languages from '../../arrays/languages.json'
import genres from '../../arrays/genres.json'
import { Link } from 'react-router-dom';
import { getDownloadURL, uploadBytes, ref as sRef } from 'firebase/storage'
import JSZip from 'jszip';
import axios from 'axios';
import FileSaver from 'file-saver';
import Pagination from '../Paginations';
import { getRelease, getReleases } from '../../api/releases';
import { useQuery } from '@tanstack/react-query'
import { CircularProgress, Alert } from '@mui/material'

const AdminReleases = () => {

    const [releases, setReleases] = useState([])
    const [users, setUsers] = useState([])
    const [platforms, setPlatforms] = useState([])
    const [tab, setTab] = useState(1)
    
    // Pagination and sorting state
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(25)
    const [searchTerm, setSearchTerm] = useState('')
    const [sortBy, setSortBy] = useState('original_date')
    const [sortOrder, setSortOrder] = useState('desc')
    const [statusFilter, setStatusFilter] = useState('')

    const statusClasses = {
        Distributed: 's-green',
        Processing: 's-blue',
        Error: 's-red n-right',
        Takedown: 's-orange'
    }

    // Create query params for the API call
    const getQueryParams = () => {
        return {
            page: currentPage,
            limit: itemsPerPage,
            search: searchTerm || undefined,
            status: tab === 2 ? 'PROCESSING' : undefined,
            sortBy: sortBy,
            sortOrder: sortOrder
        };
    };

    const { 
        data, 
        isLoading, 
        error,
        refetch 
    } = useQuery({
        queryKey: ['releases', currentPage, itemsPerPage, searchTerm, tab, sortBy, sortOrder],
        queryFn: () => getReleases(getQueryParams()),
        keepPreviousData: true,
    
    });
    useEffect(() => {
        console.log(data, "data")
        if(data && data.data?.pagination){
            setTotalPages(data.data?.pagination.totalPages);
            setTotalItems(data.data?.pagination.totalItems);
            setItemsPerPage(data.data?.pagination.itemsPerPage);
        }
    },[data])

    // Format the releases data when it changes
    useEffect(() => {
        if (data && data.data && data.data.releases) {
            const formattedReleases = data.data.releases.map(releaseIn => ({
                ...releaseIn,
                email: releaseIn.users_profile?.email || 'N/A',
                key: releaseIn?.users_profile?.user_id || '',
                index: releaseIn.id,
                status: releaseIn.status.toLowerCase().charAt(0).toUpperCase() + releaseIn.status.toLowerCase().slice(1),
                originalDate: releaseIn.original_date,
                digitalDate: releaseIn.digital_date,
                primaryGenre: releaseIn.primary_genre,
            }));
            setReleases(formattedReleases);
        }
    }, [data]);

    // Handle search input change
    const handleSearch = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        setCurrentPage(1); // Reset to first page when search changes
    }

    // Handle sort column click
    const handleSort = (property) => {
        // Toggle sort order if clicking the same column
        if (sortBy === property) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new sort column and default to ascending
            setSortBy(property);
            setSortOrder('asc');
        }
        setCurrentPage(1); // Reset to first page when sort changes
    }

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const exportZip = async (release) => {
        console.log(release, "exporting started")
        const zip = new JSZip()
        const artworkBlob = await axios.get(release.artwork, { responseType: 'blob' });
        const fileType = artworkBlob.data.type.split('/').pop().toLowerCase().replace('jpeg', 'jpg')
        zip.file(`${release.title || 'cover'}.${fileType}`, artworkBlob.data)

        const tracksCSV = []

        const releasePlatformsTitles = []

        if (release.platforms) {
            release.platforms.forEach(platform => {
                if (platform.status === 'Processing' || platform.status === 'Taken Down') {
                    const platformItem = platforms.find(item => item.id === platform.id)
                    if (platformItem) {
                        releasePlatformsTitles.push(platformItem.title)
                    }
                }
            })
        }

        const tracks = release.tracks;
        const releaseArtistsNames = release.artists ? release.artists.map(artist => `${artist.role}: ${artist.name}`) : []
        const releaseArtists = releaseArtistsNames.join(' / ')
        const releasePlatforms = releasePlatformsTitles.join(' / ')
        for (const track of tracks) {
            console.log(track, "track")
            if (!track.file && track.file !== undefined && track.file !== null) {
                console.log(track.file, "track.file")
                const trackAudioBlob = await axios.get(track.file, { responseType: 'blob' });
                const path = new URL(track.file).pathname;
                const fileName = path.split('/').pop();
                const fileExtension = fileName.split('.').pop().toLowerCase();

                
                zip.file(`${track.title}.${fileExtension}`, trackAudioBlob.data);
            }
            const trackArtistsNames = track.artists ? track.artists.map(artist => `${artist.role}: ${artist.name}`) : []
            const trackArtists = trackArtistsNames.join(' / ')
            const territories = release.countries.join(' / ')
            tracksCSV.push({
                'Release title': release.title,
                'User email': release.email,
                'UPC': release.upc,
                'Release cover': release.artwork,
                'Label name': release.label,
                'Primary genre': release.primaryGenre,
                'Secondary genre': release.secondaryGenre,
                'Release artists': releaseArtists,
                'Platforms': releasePlatforms,
                'Explicit lyrics': track.explicit,
                'Territories': territories,
                'Track title': track.title,
                'Track filename': track.filename,
                'Track duration': track.duration,
                'Track genre': track.genre,
                'Track language': track.trackLanguage,
                'Start point time': track.clipStart,
                'ISRC code': track.isrc,
                'Track artists': trackArtists,
                'Copyright year': release.copyrightYear,
                'Copyright line': release.copyrightLine,
                'Publishing year': release.publishingYear,
                'Publishing line': release.publishingLine,
                'Original release date': release.originalDate,
                'Digital release date': release.digitalDate
            });
        }

        const tracksCSVContent = arrayToCSV(tracksCSV);
        zip.file(`${release.title}.csv` || 'tracks.csv', tracksCSVContent);
        
        zip.generateAsync({ type: 'blob' }).then((content) => {
            // Save or download the ZIP file
            FileSaver.saveAs(content, release.title || 'release.zip');
        });
    }

    const arrayToCSV = (array) => {
        const header = Object.keys(array[0]).join(',');
        const rows = array.map(obj => Object.values(obj).join(',')).join('\n');
        return `${header}\n${rows}`;
    };

    return (
        <div className="admin-page py-5">
            <div className='container'>
                <h1 className='dash-title'>Releases</h1>
                <div className="row">
                    <div className="col-12 mt-4">
                        <div className="dash-card">
                            <div className="dash-card-heading mb-2">
                                <h2 className="dash-card-title">Releases</h2>
                            </div>

                            <input 
                                type="text" 
                                className='dash-input' 
                                onChange={handleSearch} 
                                value={searchTerm}
                                placeholder='Filter by Title, UPC...' 
                            />

                            <div className="dash-tabs">
                                <Link 
                                    to={'#'} 
                                    className={`dash-tab ${tab === 1 && 'active'}`}
                                    onClick={() => {
                                        setTab(1)
                                        setCurrentPage(1)
                                    }}
                                >
                                    All
                                </Link>
                                <Link 
                                    to={'#'} 
                                    className={`dash-tab ${tab === 2 && 'active'}`}
                                    onClick={() => {
                                        setTab(2)
                                        setCurrentPage(1)
                                    }}
                                >
                                    Processing
                                </Link>
                            </div>

                            {/* Loading State */}
                            {isLoading && (
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    padding: '50px 0' 
                                }}>
                                    <CircularProgress />
                                </div>
                            )}

                            {/* Error State */}
                            {error && (
                                <Alert 
                                    severity="error" 
                                    sx={{ margin: '20px 0' }}
                                >
                                    Error loading releases: {error.message}
                                </Alert>
                            )}

                            {/* Data Table */}
                            {!isLoading && !error && (
                                <>
                                    <div className="table-responsive mt-3">
                                        <table className="table dash-table">
                                            <thead>
                                                <tr>
                                                    <td>Artwork</td>
                                                    <td>
                                                        <div className='table-sort' onClick={() => handleSort('users_profile')}>
                                                            User
                                                            <i className={`fi fi-ss-angle-small-${sortOrder === "asc" ? "down" : "up"}`}></i>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className='table-sort' onClick={() => handleSort('title')}>
                                                            Title
                                                            <i className={`fi fi-ss-angle-small-${sortOrder === "asc" ? "down" : "up"}`}></i>
                                                        </div>
                                                    </td>
                                                    <td>Genre</td>
                                                    <td>
                                                        <div className='table-sort' onClick={() => handleSort('original_date')}>
                                                            Date
                                                            <i className={`fi fi-ss-angle-small-${sortOrder === "asc" ? "down" : "up"}`}></i>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className='table-sort' onClick={() => handleSort('status')}>
                                                            Status
                                                            <i className={`fi fi-ss-angle-small-${sortOrder === "asc" ? "down" : "up"}`}></i>
                                                        </div>
                                                    </td>
                                                    <td>Action</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {releases.map((release, i) => (
                                                    <tr key={i}>
                                                        <td>
                                                            <div className="table-item">
                                                                <img className='release-img' src={release.artwork} alt="" />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">{release.email}</div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">{release.title}</div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">{release.primaryGenre}</div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">{new Date(release.originalDate).toLocaleDateString()}</div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">
                                                                <div className={`release-status ${statusClasses[release.status]}`}>
                                                                    {release.status}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item d-flex">
                                                                <Link 
                                                                    to={`edit/${release.key}/${release.index}`}
                                                                    className="table-action-btn a-blue me-1"
                                                                >
                                                                    <i className="fi fi-rr-pencil"></i>
                                                                </Link>
                                                                <button 
                                                                    className="table-action-btn a-green"
                                                                    onClick={() => exportZip(release)}
                                                                >
                                                                    <i className="fi fi-br-download"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Updated Pagination that uses the pagination data from API */}
                                    <div className="pagination-info text-center mb-2">
                                        Showing {releases.length} of {totalItems} releases
                                    </div>
                                    <Pagination 
                                        currentPage={currentPage} 
                                        totalPages={totalPages} 
                                        handlePageChange={handlePageChange} 
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminReleases